import {
  Avatar,
  Box,
  Checkbox,
  Flex,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
} from "@chakra-ui/react";
import moment from "moment-timezone";
import { useMemo } from "react";
import PersonDetails from "./PersonDetails.jsx";

// Reusable row component
const InfoRow = ({ label, value }) => (
  <Flex alignItems="center" justifyContent="space-evenly" fontSize="sm" my={1}>
    <Box textAlign="left" w="40%">
      {label}
    </Box>
    <Box textAlign="center"> : </Box>
    <Box textAlign="left" width="60%" ml={4}>
      <Text fontSize="sm">{value || "-"}</Text>
    </Box>
  </Flex>
);

// Generate a unique color based on a string with better distribution
const getColorFromString = (str) => {
  if (!str) return "#718096"; // default gray

  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash; // Convert to 32bit integer
  }

  // Use a larger set of distinct colors
  const colors = [
    "#E53E3E", // red
    "#DD6B20", // orange
    "#D69E2E", // yellow
    "#38A169", // green
    "#319795", // teal
    "#3182CE", // blue
    "#5A67D8", // indigo
    "#805AD5", // purple
    "#D53F8C", // pink
    "#C05621", // brown
    "#2C7A7B", // cyan
    "#2B6CB0", // darkblue
    "#6B46C1", // violet
    "#B83280", // magenta
    "#9B2C2C", // darkred
    "#744210", // darkorange
    "#276749", // darkgreen
    "#234E52", // darkteal
  ];

  // Use hash to pick a color
  const index = Math.abs(hash) % colors.length;
  return colors[index];
};

const ProfileCard = ({ item, onSelectionChange, isSelected, isFirst }) => {
  const {
    FIRST_NAME,
    LAST_NAME,
    MIDDLE_NAME,
    GENDER,
    ALIAS,
    BIRTH_DATE,
    MATCH_SCORE,
    MODIFIED_BY,
    PERSON_ID,
  } = item;

  // Generate a consistent color based on person ID or name
  const avatarColor = useMemo(() => {
    const identifier = PERSON_ID || `${FIRST_NAME}${LAST_NAME}`;
    return getColorFromString(identifier);
  }, [PERSON_ID, FIRST_NAME, LAST_NAME]);

  const handleCheckboxChange = (event) => {
    if (onSelectionChange) {
      // Always pass the item along with its avatar color
      onSelectionChange({ ...item, avatarColor }, event.target.checked);
    }
  };

  return (
    <Box
      p={2}
      bg="gray.100"
      boxShadow="md"
      borderRadius="md"
      textAlign="left"
      mt={2}
      display="flex"
      alignItems="center"
      flexDir="column"
      h="240px"
      w="100%"
      cursor="pointer"
    >
      {/* Header */}
      <Flex alignItems="center" justify="space-between" w="100%" fontSize="sm">
        <Text>{MATCH_SCORE && `Match Score : ${Math.round(MATCH_SCORE)}`}</Text>

        <Checkbox
          colorScheme="green"
          size="sm"
          isChecked={isSelected}
          onChange={handleCheckboxChange}
          mb={1}
          isDisabled={isFirst}
        >
          Select
        </Checkbox>
      </Flex>

      {/* Avatar with consistent color */}
      <Box w="100%" display="flex" alignItems="center" justifyContent="center">
        <Avatar
          mb={1}
          size="md"
          name={`${FIRST_NAME} ${MIDDLE_NAME} ${LAST_NAME}`}
          bg={avatarColor}
          color="white"
        />
      </Box>

      {/* Info Section wrapped in Popover */}
      <Popover placement="bottom" trigger="hover" closeOnBlur={false} p={0}>
        <PopoverTrigger>
          <Box w="100%" mx="auto" cursor="pointer" _hover={{ bg: "gray.50" }}>
            <InfoRow
              label="Name"
              value={`${FIRST_NAME} ${MIDDLE_NAME || ""} ${LAST_NAME}`}
            />
            <InfoRow label="Gender" value={GENDER} />
            <InfoRow
              label="Birth Date"
              value={moment(BIRTH_DATE, "YYYYMMDD").format("MM/DD/YYYY")}
            />
            <InfoRow label="Alias" value={ALIAS} />
            <InfoRow label="Modified By" value={MODIFIED_BY} />
          </Box>
        </PopoverTrigger>

        <PopoverContent color="black" w="1000px" p={0} border="1px solid gray">
          <PopoverArrow />
          <PopoverBody>
            <Box maxH="330px" overflowY="auto">
              <PersonDetails person={item} />
            </Box>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
};

export default ProfileCard;
